import {getState, patchState, signalStore, withHooks, withMethods, withState, withComputed} from "@ngrx/signals";
import {computed, effect} from "@angular/core";
import {UserProfile, Token, Condominium, Tenant} from '@core/types';
import { EncryptionService } from '@shared/services';
import {STORE} from '@core/constans'



type ProfileState = {
  data: UserProfile;
};

const initialState: ProfileState = {
  data: {
    userName: '',
    token: {
      value: '',
      expire: ''
    } as Token,
    email:'',
    firstName: '',
    lastName: '',
    regHash: '',
    activeTenantId:'',
    tenants:[],
    activeCondominiumId:'',
    condominiums:[],
    showTour: true
  }
};

const encryptionService = new EncryptionService();

function persistenceState(): ProfileState {

  const version = localStorage.getItem(`${STORE.profile}_ver`);
  let previousShowTour: boolean | null = null;

  if (version !== STORE.profile_version) {
    // Preservar la finalización del tour entre versiones (es preferencia del usuario,
    // no estado de sesión que deba invalidarse en cada deploy).
    const previous = localStorage.getItem(STORE.profile);
    if (previous) {
      try {
        const decoded = encryptionService.decryptJson(previous) as ProfileState | null;
        previousShowTour = decoded?.data?.showTour ?? null;
      } catch {
        previousShowTour = null;
      }
    }
    localStorage.removeItem(STORE.profile);
    localStorage.setItem(`${STORE.profile}_ver`,STORE.profile_version);
  }

  const savedState = localStorage.getItem(STORE.profile);
  if (savedState) {
    try {
      return encryptionService.decryptJson(savedState) || initialState;
    } catch {
      return initialState;
    }
  }

  if (previousShowTour !== null) {
    return { data: { ...initialState.data, showTour: previousShowTour } };
  }

  return initialState;
}

function saveState(state: ProfileState) {
  const encrypted = encryptionService.encryptJson(state);
  localStorage.setItem(STORE.profile, encrypted);
  localStorage.setItem(`${STORE.profile}_ver`,STORE.profile_version);
}

export const profileStore = signalStore(
  { providedIn: 'root'},
  withState(persistenceState()),
  withComputed((store) => ({
    activeTenant: computed(() => {
      const activeId = store.data.activeTenantId();
      return store.data.tenants().find(t => t.id === activeId) || null;
    })
  })),
  withMethods((store) => ({
    setProfile(profile: UserProfile) {
      patchState(store, (state) => ({
        data: {
          ...state.data,
          ...profile
        }
      }));
    },

   setToken(token: Token) {
      patchState(store, (state) => ({
        data: {
          ...state.data,
          token: token
        }
      }));
    },

    setCondominiums(condominiums: Condominium[]) {
      patchState(store, (state) => ({
        data: {
          ...state.data,
          condominiums:condominiums
        }
      }));
    },

    setActiveCondominium(activeCondominiumId: string) {
      patchState(store, (state) => ({
        data: {
          ...state.data,
          activeCondominiumId:activeCondominiumId
        }
      }));
    },

    setUserInfo(userName: string, email: string, firstName: string, lastName: string, regHash: string, activeTenantId:string, tenants: Tenant[]) {
      patchState(store, (state) => ({
        data: {
          ...state.data,
          userName,
          email,
          firstName,
          lastName,
          regHash,
          activeTenantId,
          tenants
        }
      }));
    },

    setActiveTenant(tenantId: string) {
      patchState(store, (state) => ({
        data: {
          ...state.data,
          activeTenantId: tenantId,
          condominiums: [], // Limpiar condominios para forzar recarga
          activeCondominiumId: '' // Limpiar condominio activo
        }
      }));
    },


    setRegHash(regHash: string) {
      patchState(store, (state) => ({
        data: {
          ...state.data,
          regHash: regHash
        }
      }));
    },


    dismissTour() {
      patchState(store, (state) => ({
        data: { ...state.data, showTour: false }
      }));
    },

    clearProfile() {
      // Preservar showTour: si el usuario ya completó el tour, no debe volver a verlo
      // tras cerrar sesión.
      const currentShowTour = store.data.showTour();
      patchState(store, () => ({
        data: { ...initialState.data, showTour: currentShowTour }
      }));
      localStorage.removeItem(STORE.profile);
    },


  })),
  withHooks({
    onInit(store) {
      effect(() => {
        const state = getState(store);
        saveState(state);
      });
    },
  })
)

